/**
 * MilestoneToolDialog — OOH AI Toolkit modal.
 *
 * Opens from a milestone's "Get AI tool" button. The founder types a
 * 2-4 sentence business brief; Yusuf drafts the artefact. The founder
 * can then either:
 *   • Copy individual fields or the whole draft and use it externally
 *   • Hit "Looks good — mark cleared" to save the JSON draft as the
 *     milestone's artefact-of-record AND tick the milestone in one
 *     motion. The roadmap parent is refreshed via `onCleared(rmDoc)`.
 *
 * Backend:
 *   POST /api/scaling-lean/roadmap/{startup_id}/milestone-tool          (generate)
 *   POST /api/scaling-lean/roadmap/{startup_id}/milestone-evidence      (save+clear)
 */
import React, { useState, useEffect } from 'react';
import {
  X, Sparkles, Loader2, Copy, Check, Award, FileText, RefreshCw, ShieldCheck,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';

/* eslint-disable -- v6 react-hooks/set-state-in-effect false-positive on the dialog-open reset effect. */

const HUMANIZE = (k) =>
  k.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

export default function MilestoneToolDialog({
  open,
  onClose,
  startupId,
  milestone,
  toolCatalog, // { milestone_id: { tool_label, hint, schema_keys } }
  onCleared,   // (rmDoc) => void  — parent refreshes its roadmap state
  isCleared,   // bool — if the milestone is already cleared (changes CTA copy)
}) {
  const [brief, setBrief] = useState('');
  const [busy, setBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const [draft, setDraft] = useState(null);
  const [copiedKey, setCopiedKey] = useState(null);

  // Reset the form whenever the dialog opens for a new milestone
  useEffect(() => {
    if (open) {
      setBrief('');
      setDraft(null);
      setCopiedKey(null);
    }
  }, [open, milestone?.id]);

  if (!open || !milestone) return null;

  const cfg = toolCatalog?.[milestone.id];
  const toolLabel = cfg?.tool_label || `AI draft — ${milestone.label}`;
  const hint = cfg?.hint || 'Describe your business so Yusuf can draft this artefact.';

  const generate = async () => {
    if (brief.trim().length < 10) {
      toast.error('Tell Yusuf a bit more about the business (≥10 chars).');
      return;
    }
    setBusy(true);
    setDraft(null);
    try {
      const { data } = await api.post(
        `/scaling-lean/roadmap/${startupId}/milestone-tool`,
        { milestone_id: milestone.id, business_brief: brief.trim() },
      );
      setDraft(data);
      toast.success('Draft ready. Refine it, then upload the polished version.');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Draft failed. Try again.');
    } finally {
      setBusy(false);
    }
  };

  const copyField = async (key, value) => {
    try {
      const text = Array.isArray(value) ? value.join('\n') : String(value ?? '');
      await navigator.clipboard.writeText(text);
      setCopiedKey(key);
      setTimeout(() => setCopiedKey(null), 1500);
    } catch {
      toast.error('Could not copy.');
    }
  };

  const copyAll = async () => {
    if (!draft?.draft) return;
    try {
      const text = Object.entries(draft.draft)
        .map(([k, v]) => `${HUMANIZE(k)}:\n${Array.isArray(v) ? v.join('\n') : v}\n`)
        .join('\n');
      await navigator.clipboard.writeText(text);
      toast.success('Whole draft copied to clipboard.');
    } catch {
      toast.error('Could not copy.');
    }
  };

  /**
   * Save the AI draft as the milestone's artefact-of-record and tick
   * the milestone in one motion. Closes the dialog and asks the parent
   * to refresh its roadmap state via `onCleared(rmDoc)`.
   */
  const saveAsEvidence = async ({ markCleared }) => {
    if (!draft?.draft) return;
    setSaving(true);
    try {
      const { data } = await api.post(
        `/scaling-lean/roadmap/${startupId}/milestone-evidence`,
        {
          milestone_id: milestone.id,
          tool_label: draft.tool_label,
          draft: draft.draft,
          business_brief: brief.trim() || null,
          mark_cleared: !!markCleared,
        },
      );
      toast.success(
        markCleared
          ? `${milestone.label} cleared — AI artefact saved.`
          : 'AI artefact saved to milestone evidence.',
      );
      if (typeof onCleared === 'function') onCleared(data);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save evidence.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 sm:p-8"
      onClick={onClose}
      data-testid="milestone-tool-dialog"
    >
      <div
        className="relative max-w-3xl w-full max-h-[92vh] overflow-y-auto rounded-2xl bg-white dark:bg-[#0d0d0d] shadow-2xl border border-[#8B3FC5]/40"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 bg-white/95 dark:bg-[#0d0d0d]/95 backdrop-blur-sm border-b border-slate-200 dark:border-white/10 px-6 py-4 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] font-bold text-[#5E1F8B] dark:text-[#D4AF37]">
              <Award size={11} /> Patent Pending · OOH™ AI Toolkit
            </div>
            <h2 className="display text-xl sm:text-2xl font-bold mt-1.5 leading-tight">{toolLabel}</h2>
            <p className="text-xs text-slate-500 dark:text-white/55 mt-1">
              For milestone: <span className="font-bold text-slate-700 dark:text-white/80">{milestone.label}</span>
            </p>
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            data-testid="milestone-tool-close"
            className="h-9 w-9 rounded-full bg-slate-100 dark:bg-white/10 hover:bg-slate-200 dark:hover:bg-white/20 flex items-center justify-center shrink-0"
          >
            <X size={16} />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 space-y-5">
          {/* Input */}
          <div>
            <label className="block text-[11px] uppercase tracking-[0.22em] font-bold text-slate-500 dark:text-white/55 mb-1.5">
              Your business brief
            </label>
            <p className="text-xs text-slate-500 dark:text-white/55 mb-2">{hint}</p>
            <textarea
              value={brief}
              onChange={(e) => setBrief(e.target.value)}
              data-testid="milestone-tool-brief"
              placeholder="e.g. We're building a sovereign AI governance OS for Qatari family offices. Founders we've spoken to all describe board reporting as 'PDF + WhatsApp chaos'…"
              className="w-full min-h-[120px] rounded-md border border-slate-300 dark:border-white/15 bg-white dark:bg-[#1a1a1a] px-3 py-2 text-sm focus:outline-none focus:border-[#8B3FC5] resize-y"
              maxLength={2000}
            />
            <div className="flex items-center justify-between mt-1">
              <span className="text-[10px] text-slate-400 dark:text-white/40">{brief.length}/2000</span>
              <button
                onClick={generate}
                disabled={busy || brief.trim().length < 10}
                data-testid="milestone-tool-generate"
                className="ve-btn-primary text-sm inline-flex items-center gap-1.5 disabled:opacity-50"
              >
                {busy
                  ? <><Loader2 size={13} className="animate-spin" /> Yusuf is drafting…</>
                  : draft
                    ? <><RefreshCw size={13} /> Regenerate</>
                    : <><Sparkles size={13} /> Generate draft</>}
              </button>
            </div>
          </div>

          {/* Draft output */}
          {draft && (
            <div className="border-t border-slate-200 dark:border-white/10 pt-5" data-testid="milestone-tool-draft">
              <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
                <div className="flex items-center gap-2">
                  <FileText size={14} className="text-[#5E1F8B] dark:text-[#8B3FC5]" />
                  <h3 className="font-bold text-sm">Your draft is ready</h3>
                  <span className="text-[9px] uppercase tracking-[0.22em] px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-300 font-bold">
                    AI-assisted
                  </span>
                </div>
                <button
                  onClick={copyAll}
                  data-testid="milestone-tool-copy-all"
                  className="text-xs inline-flex items-center gap-1 text-[#5E1F8B] dark:text-[#8B3FC5] hover:underline font-bold"
                >
                  <Copy size={11} /> Copy all
                </button>
              </div>
              <div className="space-y-3">
                {Object.entries(draft.draft || {}).map(([k, v]) => {
                  const human = HUMANIZE(k);
                  const isArr = Array.isArray(v);
                  const display = isArr
                    ? v
                    : (typeof v === 'string' ? v : JSON.stringify(v, null, 2));
                  return (
                    <div
                      key={k}
                      className="rounded-lg border border-slate-200 dark:border-white/10 bg-slate-50/60 dark:bg-white/5 p-4"
                      data-testid={`milestone-tool-field-${k}`}
                    >
                      <div className="flex items-center justify-between gap-2 mb-2">
                        <span className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500 dark:text-white/55">
                          {human}
                        </span>
                        <button
                          onClick={() => copyField(k, v)}
                          className="text-[10px] inline-flex items-center gap-1 text-slate-500 hover:text-[#5E1F8B] dark:hover:text-[#8B3FC5] font-bold"
                        >
                          {copiedKey === k ? <><Check size={10} /> Copied</> : <><Copy size={10} /> Copy</>}
                        </button>
                      </div>
                      {isArr ? (
                        <ul className="space-y-1">
                          {display.map((item, i) => (
                            <li key={`${k}-${i}`} className="text-sm text-slate-700 dark:text-white/80 flex gap-2">
                              <span className="text-[#8B3FC5] font-bold">·</span>
                              <span>{String(item)}</span>
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <p className="text-sm text-slate-700 dark:text-white/80 whitespace-pre-line">{display}</p>
                      )}
                    </div>
                  );
                })}
              </div>
              <p className="mt-4 text-[10px] uppercase tracking-[0.22em] text-slate-400 dark:text-white/40 font-bold">
                Use this as a starting point. Refine, validate with customers, then save the polished artefact to clear the milestone.
              </p>

              {/* ─── Save & Clear CTAs ───
                  Closes the loop from "blank checklist" → "AI draft" →
                  "cleared with evidence" in one motion. The JSON draft
                  becomes the milestone's artefact-of-record (timestamped,
                  signed with the saving user's id). */}
              <div className="mt-5 flex flex-col sm:flex-row gap-3 items-stretch sm:items-center justify-end border-t border-slate-200 dark:border-white/10 pt-4">
                <button
                  type="button"
                  onClick={() => saveAsEvidence({ markCleared: false })}
                  disabled={saving}
                  data-testid="milestone-tool-save-only"
                  className="text-xs font-bold text-slate-600 dark:text-white/70 hover:text-[#5E1F8B] dark:hover:text-[#D4AF37] disabled:opacity-50 px-3 py-2 transition"
                >
                  Save draft only
                </button>
                <button
                  type="button"
                  onClick={() => saveAsEvidence({ markCleared: true })}
                  disabled={saving}
                  data-testid="milestone-tool-save-clear"
                  className="ve-btn-primary text-sm inline-flex items-center gap-1.5 disabled:opacity-50"
                >
                  {saving
                    ? <><Loader2 size={13} className="animate-spin" /> Saving…</>
                    : <><ShieldCheck size={13} /> {isCleared ? 'Save & keep cleared' : 'Looks good — mark cleared'}</>}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
